/*
 * Cyphon hero (sections/cy-hero.liquid): slides, swipe, mouse parallax, transparent-header
 * observer, a lite YouTube modal and lazy GSAP motion. The iframe is created only on click.
 */
(() => {
  const h = document.querySelector('[data-cy-hero]');
  if (!h) return;

  const $$ = (s, r = h) => [...r.querySelectorAll(s)];
  const slides = $$('[data-cy-slide]');
  const dots = $$('[data-cy-go]');
  const n = slides.length;
  const reduce = matchMedia('(prefers-reduced-motion:reduce)').matches;
  const str = +h.dataset.depth || 0;
  let i = 0;
  let layers = [];
  let timer = 0;

  const parallaxLayers = () => {
    layers = $$('.is-active [data-p]');
  };
  parallaxLayers();

  // Slide swap. Without GSAP (or with reduced motion) only classes change and the CSS crossfade runs.
  let swap;
  let running;
  const flip = (from, to) => {
    from.classList.remove('is-active');
    from.setAttribute('aria-hidden', 'true');
    from.inert = true;
    to.classList.add('is-active');
    to.removeAttribute('aria-hidden');
    to.inert = false;
  };
  const lines = (s) => $$('.cy-hero__line>span', s);
  const copy = (s) => $$('.cy-hero__label,.cy-hero__tag,.cy-hero__text', s);
  const ctas = (s) => $$('.cy-hero__cta>*', s);

  // Slides 2+ carry placeholders so their images never compete with the first slide (the LCP).
  // fill() swaps in the real sources and resolves once the images are decoded.
  const fill = (slide) => {
    // undefined = not started, a promise = loading, null = nothing (left) to wait for
    if (slide._fill !== undefined) return slide._fill;
    const imgs = $$('img[data-src]', slide);
    if (!imgs.length) return (slide._fill = null);
    $$('source[data-srcset]', slide).forEach((el) => (el.srcset = el.dataset.srcset));
    imgs.forEach((el) => {
      el.srcset = el.dataset.srcset;
      el.src = el.dataset.src;
    });
    return (slide._fill = Promise.all(imgs.map((el) => el.decode().catch(() => {}))).then(() => {
      slide._fill = null;
    }));
  };
  const later = slides.filter((el) => el.querySelector('img[data-src]'));
  const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 4000 }) : setTimeout(fn, 1500));
  // Only the next slide is fetched ahead, so phones never download all slides up front.
  const warmNext = () => {
    const next = slides[(i + 1) % n];
    if (next !== slides[i]) idle(() => fill(next));
  };
  if (later.length) document.readyState === 'complete' ? idle(warmNext) : addEventListener('load', () => idle(warmNext), { once: true });

  let busy = false;
  const go = (k) => {
    k = (k + n) % n;
    if (k === i || busy) return;
    const wait = fill(slides[k]);
    if (wait) {
      // the slide's images are still loading: show it once they are ready (no empty flash)
      busy = true;
      wait.then(() => {
        busy = false;
        go(k);
      });
      return;
    }
    const from = slides[i];
    i = k;
    const to = slides[i];
    dots.forEach((d, j) => (j === i ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
    h.style.setProperty('--i', i);
    if (running) running.progress(1); // finish a swap that is still running (fast swipes / clicks)
    if (swap) swap(from, to);
    else flip(from, to);
    parallaxLayers();
    started = true; // any swap (counter, swipe, timer) means autoplay is on from here
    play();
    if (later.length) warmNext();
  };

  // Autoplay: pauses only while the tab is hidden. It does not start until the first user
  // interaction or window load + 12 s (whichever is first), so a later slide never becomes the LCP.
  let started = false;
  const play = () => {
    clearInterval(timer);
    if (started && h.dataset.autoplay === 'true' && n > 1 && !reduce && !document.hidden) {
      timer = setInterval(() => go(i + 1), (+h.dataset.speed || 6) * 1000);
    }
  };

  // Motion: GSAP + ScrollTrigger (assets/gsap.min.js, assets/ScrollTrigger.min.js) are fetched only
  // by this section, after the page has loaded and the hero is within 300px of the viewport.
  // Only transform and opacity are animated. gsap.matchMedia gives phones shorter, vertical motion.
  const initMotion = () => {
    const { gsap, ScrollTrigger } = window;
    if (ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
    h.classList.add('cy-gsap'); // GSAP now owns the slide fade (CSS transition off)
    // two conditions: with only one, the callback never runs on the other screen size
    gsap.matchMedia().add({ wide: '(min-width:768px)', narrow: '(max-width:767px)' }, (ctx) => {
      const { wide } = ctx.conditions;
      const d = wide ? 1 : 0.9;
      swap = (from, to) => {
        const prod = to.querySelector('.cy-hero__product');
        const bg = to.querySelector('.cy-hero__bg');
        const fromProd = from.querySelector('.cy-hero__product');
        const fromCard = from.querySelector('.cy-hero__card');
        const all = lines(to);
        const sweep = all.slice(-1);
        const body = all.slice(0, -1);
        // The new slide fades in OVER the old one (which stays opaque underneath), so anything the two
        // share, such as the stats strip, never dips or moves. Slide-specific parts fade out underneath.
        // Phones get the same choreography with smaller distances; the moving layers are promoted for the swap only.
        gsap.set(to, { opacity: 0, visibility: 'visible', zIndex: 2, willChange: 'opacity' });
        if (wide) gsap.set(bg, { willChange: 'transform' });
        to.classList.add('is-active');
        to.removeAttribute('aria-hidden');
        to.inert = false;
        const tl = gsap
          .timeline({
            defaults: { ease: 'power3.out' },
            onComplete: () => {
              flip(from, to);
              // incoming keeps opacity 1 (inline, then CSS .is-active), so clearing causes no pop
              gsap.set([from, to], { clearProps: 'opacity,visibility,zIndex,willChange' });
              gsap.set(bg, { clearProps: 'willChange' });
              gsap.set([...copy(from), fromProd, fromCard].filter(Boolean), { clearProps: 'opacity' });
              gsap.set(copy(from), { y: 0 });
              gsap.set([...lines(from), ...ctas(from), ...$$('.cy-hero__cta svg', from)], { clearProps: 'opacity,transform' });
              running = null;
            },
          })
          .to(to, { opacity: 1, duration: 0.7 * d, ease: 'power1.inOut' }, 0);
        if (wide) {
          // Desktop: full choreography, outgoing parts leave underneath while the incoming slide fades in on top.
          tl.to(copy(from), { y: wide ? -16 : -10, opacity: 0, duration: 0.5 * d, ease: 'power2.in' }, 0)
            .to([fromProd, fromCard].filter(Boolean), { opacity: 0, duration: 0.5 * d, ease: 'power2.in' }, 0)
            .to(lines(from), { yPercent: -110, duration: 0.45 * d, ease: 'power2.in', stagger: 0.05 }, 0)
            .fromTo(bg, { scale: wide ? 1.08 : 1.05 }, { scale: 1, duration: 1.6 * d }, 0)
            .fromTo(prod, { x: wide ? 70 : 0, y: wide ? 0 : 30, scale: 0.94, opacity: 0 }, { x: 0, y: 0, scale: 1, opacity: 1, duration: 0.95 * d }, 0.15)
            // heading: lines rise out of their masks, the gradient line sweeps in from the left
            .fromTo(body, { yPercent: 110 }, { yPercent: 0, duration: 0.8 * d, ease: 'power4.out', stagger: 0.09 }, 0.2)
            .fromTo(sweep, { xPercent: -105 }, { xPercent: 0, duration: 0.9 * d, ease: 'power4.out' }, 0.2 + 0.09 * body.length + 0.05)
            .fromTo(copy(to), { y: wide ? 28 : 14, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7 * d, stagger: 0.07 }, 0.3)
            // the Shop Now button is NOT animated: it just rides along with its slide's fade
            // card: on phones it sits in the same pinned spot as the old one, so it comes in early (no empty gap)
            .fromTo($$('.cy-hero__card', to), { y: wide ? 20 : 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6 * d, stagger: 0.1 }, wide ? 0.45 : 0.2);
        } else {
          // Phones: lighter motion, same feel. The outgoing slide stays untouched underneath (no dip); on top the
          // heading, text, buttons, card and product fade in with a short rise. No masks, no zoom, no scale:
          // every extra repainted layer cost ~10 ms per frame on a 4x-throttled CPU.
          tl.fromTo(prod, { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8 * d }, 0.1)
            .fromTo($$('.cy-hero__title', to), { y: 16, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7 * d }, 0.15)
            .fromTo(copy(to), { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6 * d, stagger: 0.06 }, 0.25)
            .fromTo($$('.cy-hero__card', to), { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6 * d }, 0.3);
        }
        running = tl;
      };
      if (wide && ScrollTrigger) {
        // Scroll parallax: background and product drift at different speeds as the hero scrolls away
        const trigger = { trigger: h, start: 'top top', end: 'bottom top', scrub: true };
        gsap.to($$('.cy-hero__bg'), { yPercent: 8, ease: 'none', scrollTrigger: trigger });
        gsap.to($$('.cy-hero__product'), { yPercent: 6, ease: 'none', scrollTrigger: trigger });
      }
      return () => {
        swap = null;
      };
    });
  };
  const load = (src) =>
    new Promise((ok, no) => {
      const el = document.createElement('script');
      el.src = src;
      el.onload = ok;
      el.onerror = no;
      document.head.append(el);
    });
  if (h.dataset.gsap && !reduce && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        const run = () =>
          load(h.dataset.gsap)
            .then(() => (matchMedia('(min-width:768px)').matches ? load(h.dataset.st) : 0))
            .then(initMotion)
            .catch(() => {});
        const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 2500 }) : setTimeout(run, 400));
        document.readyState === 'complete' ? idle() : addEventListener('load', idle, { once: true });
      },
      { rootMargin: '300px' }
    );
    io.observe(h);
  }

  document.addEventListener('visibilitychange', play);
  const startPlay = () => {
    if (started) return;
    started = true;
    ['scroll', 'touchstart', 'pointerdown', 'keydown'].forEach((t) => removeEventListener(t, startPlay));
    play();
  };
  ['scroll', 'touchstart', 'pointerdown', 'keydown'].forEach((t) => addEventListener(t, startPlay, { passive: true }));
  const later12 = () => setTimeout(startPlay, 12000);
  document.readyState === 'complete' ? later12() : addEventListener('load', later12, { once: true });

  // Swipe (touch / pen only).
  let sx = 0;
  let sy = 0;
  h.addEventListener(
    'pointerdown',
    (e) => {
      sx = e.clientX;
      sy = e.clientY;
    },
    { passive: true }
  );
  h.addEventListener(
    'pointerup',
    (e) => {
      const dx = e.clientX - sx;
      if (e.pointerType !== 'mouse' && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(e.clientY - sy) * 1.5) {
        go(i + (dx < 0 ? 1 : -1));
      }
    },
    { passive: true }
  );

  // Mouse parallax: fine pointers only; the loop stops once the layers settle.
  if (str && !reduce && matchMedia('(pointer:fine)').matches) {
    let tx = 0;
    let ty = 0;
    let x = 0;
    let y = 0;
    let raf = 0;
    let rect;
    const loop = () => {
      x += (tx - x) * 0.08;
      y += (ty - y) * 0.08;
      layers.forEach((l) => {
        const d = str * 1.8 * (parseFloat(l.dataset.p) || 0);
        l.style.translate = `${-x * d}px ${-y * d * 0.75}px`; // not transform: GSAP owns that
      });
      raf = Math.abs(tx - x) + Math.abs(ty - y) > 0.001 ? requestAnimationFrame(loop) : 0;
    };
    const kick = () => {
      raf = raf || requestAnimationFrame(loop);
    };
    h.addEventListener('pointerenter', () => {
      rect = h.getBoundingClientRect();
    });
    h.addEventListener(
      'pointermove',
      (e) => {
        if (e.pointerType !== 'mouse' || !rect) return;
        tx = (e.clientX - rect.left) / rect.width - 0.5;
        ty = (e.clientY - rect.top) / rect.height - 0.5;
        kick();
      },
      { passive: true }
    );
    h.addEventListener('pointerleave', () => {
      tx = ty = 0;
      kick();
    });
  }

  // Header: transparent over the hero, white glass once the sentinel leaves the viewport.
  const header = document.getElementById('page-header');
  const sentinel = h.querySelector('[data-cy-sentinel]');
  if (header && sentinel && 'IntersectionObserver' in window) {
    new IntersectionObserver(([e]) => header.classList.toggle('cy-hdr-solid', !e.isIntersecting)).observe(sentinel);
  }

  // Video modal: the iframe exists only while the modal is open.
  const root = h.parentElement;
  const vm = root.querySelector('[data-cy-vm]');
  const box = vm && vm.querySelector('[data-cy-vm-box]');
  let opener;
  const openVideo = (btn) => {
    const m = /(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/.exec(btn.dataset.cyVideo);
    if (!m || !vm) return;
    opener = btn;
    box.innerHTML = `<iframe src="https://www.youtube-nocookie.com/embed/${m[1]}?autoplay=1&rel=0" title="${vm.getAttribute('aria-label')}" allow="autoplay;encrypted-media;picture-in-picture;fullscreen" allowfullscreen></iframe>`;
    vm.hidden = false;
    document.documentElement.classList.add('cy-vm-lock');
    vm.querySelector('[data-cy-vm-x]').focus();
  };
  const closeVideo = () => {
    vm.hidden = true;
    box.innerHTML = '';
    document.documentElement.classList.remove('cy-vm-lock');
    if (opener) opener.focus();
  };

  root.addEventListener('click', (e) => {
    const t = e.target;
    const c = (s) => t.closest(s);
    let b;
    if ((b = c('[data-cy-go]'))) go(+b.dataset.cyGo);
    else if ((b = c('[data-cy-video]'))) openVideo(b);
    else if (vm && (c('[data-cy-vm-x]') || t === vm)) closeVideo();
    else if (c('[data-cy-next]')) {
      const next = h.closest('.shopify-section').nextElementSibling;
      if (next) next.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });
    }
  });

  root.addEventListener('keydown', (e) => {
    if (!vm || vm.hidden) return;
    if (e.key === 'Escape') closeVideo();
    else if (e.key === 'Tab') {
      // Focus stays on the close button and the player.
      const f = $$('button,iframe', vm);
      f[(f.indexOf(document.activeElement) + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
      e.preventDefault();
    }
  });
})();
